import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from "swiper/modules";
import {
  FaChevronLeft,
  FaChevronRight,
  FaCogs,
  FaLightbulb,
  FaProjectDiagram,
  FaBrain,
  FaUsers,
  FaChalkboardTeacher,
  FaDatabase,
  FaCloud,
  FaProductHunt,
  FaBusinessTime,
  FaCode,
  FaTools,
} from "react-icons/fa";
import {
  SiFigma,
  SiCoda,
  SiGithub,
  SiCanva,
  SiVscodium,
  SiMiro,
} from "react-icons/si";
import "swiper/swiper-bundle.css";

const skills = {
  soft: [
    {
      name: "Leadership",
      icon: <FaChalkboardTeacher className="text-3xl text-blue-400" />,
      description:
        "Leading teams towards success through effective strategy and guidance.",
    },
    {
      name: "Strategic Thinking",
      icon: <FaBrain className="text-3xl text-blue-400" />,
      description:
        "Developing creative and analytical solutions for complex business challenges.",
    },
    {
      name: "Communication",
      icon: <FaUsers className="text-3xl text-blue-400" />,
      description:
        "Clear and effective communication for team and stakeholder engagement.",
    },
    {
      name: "Problem Solving",
      icon: <FaLightbulb className="text-3xl text-blue-400" />,
      description: "Identifying and solving issues with a practical, creative approach.",
    },
    {
      name: "Team Collaboration",
      icon: <FaProjectDiagram className="text-3xl text-blue-400" />,
      description: "Working together seamlessly to achieve common goals and milestones.",
    },
    {
      name: "Adaptability",
      icon: <FaCogs className="text-3xl text-blue-400" />,
      description: "Adjusting quickly to changing environments and challenges with ease.",
    },
  ],
  hard: [
    {
      name: "Product Management",
      icon: <FaProductHunt className="text-3xl text-blue-400" />,
      description: "Managing product life cycles from concept to final release.",
    },
    {
      name: "Data Governance",
      icon: <FaDatabase className="text-3xl text-blue-400" />,
      description: "Ensuring data quality, security, and compliance across all platforms.",
    },
    {
      name: "Cloud Infrastructure",
      icon: <FaCloud className="text-3xl text-blue-400" />,
      description: "Designing scalable cloud architectures for modern business needs.",
    },
    {
      name: "Agile/Scrum",
      icon: <FaTools className="text-3xl text-blue-400" />,
      description: "Managing projects using Agile methodology for faster, flexible delivery.",
    },
    {
      name: "Software Development Lifecycle",
      icon: <FaCode className="text-3xl text-blue-400" />,
      description: "Full software development process from planning to deployment.",
    },
    {
      name: "Business Analysis",
      icon: <FaBusinessTime className="text-3xl text-blue-400" />,
      description: "Understanding business needs and creating value-driven solutions.",
    },
  ],
  tools: [
    {
      name: "Figma",
      icon: <SiFigma className="text-3xl text-blue-400" />,
      description: "UI/UX design tool for creating user interfaces and prototypes.",
    },
    {
      name: "Coda",
      icon: <SiCoda className="text-3xl text-blue-400" />,
      description: "All-in-one doc tool that combines documents and spreadsheets.",
    },
    {
      name: "GitHub",
      icon: <SiGithub className="text-3xl text-blue-400" />,
      description: "Version control platform for hosting and reviewing code repositories.",
    },
    {
      name: "Canva",
      icon: <SiCanva className="text-3xl text-blue-400" />,
      description: "Online design platform for creating stunning visuals and media.",
    },
    {
      name: "Visual Studio",
      icon: <SiVscodium className="text-3xl text-blue-400" />,
      description: "IDE for coding, debugging, and building applications.",
    },
    {
      name: "Miro",
      icon: <SiMiro className="text-3xl text-blue-400" />,
      description: "Collaborative online whiteboard for brainstorming and team collaboration.",
    },
  ],
};

export default function Toolbox() {
  return (
    <section className="py-16 px-12 bg-gray-100 flex justify-center">
      {/* Toolbox Container */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 p-8 rounded-lg shadow-lg max-w-[1200px] w-full relative">
        <h2 className="text-4xl font-bold text-center mb-12 text-brand-yellow">
          My Toolbox
        </h2>

        {(["soft", "hard", "tools"] as const).map((category, idx) => (
          <div key={category} className="mb-16 relative">
            <h3 className="text-2xl font-semibold mb-4 text-white text-center">
              {category === "soft" ? (
                <>
                  My <span className="text-blue-400">Soft</span> Skills
                </>
              ) : category === "hard" ? (
                <>
                  My <span className="text-brand-green">Hard</span> Skills
                </>
              ) : (
                <>
                  My <span className="text-orange-400">Favorite</span> Apps
                </>
              )}
            </h3>

            {/* Swiper for Carousel */}
            <div className="relative">
              <Swiper
                spaceBetween={20}
                slidesPerView={1} // For mobile
                loop={true}
                autoplay={{
                  delay: (idx + 1) * 4000, // Different interval for each row
                  disableOnInteraction: true,
                }}
                breakpoints={{
                  640: {
                    slidesPerView: 3, // For tablet and larger screens
                  },
                  1024: {
                    slidesPerView: 3, // For larger screens
                  },
                }}
                navigation={{
                  nextEl: `.swiper-button-next-${idx}`,
                  prevEl: `.swiper-button-prev-${idx}`,
                }}
                modules={[Navigation, Autoplay]}
              >
                {skills[category].map((item, index) => (
                  <SwiperSlide key={index}>
                    <div className="bg-white rounded-lg shadow-lg p-6 flex flex-col justify-center items-center transition-all duration-200 ease-in-out">
                      <div>{item.icon}</div>
                      <p className="mt-4 text-gray-700 font-semibold">{item.name}</p>
                      <p className="text-center text-gray-600 text-sm mt-2">
                        {item.description}
                      </p>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>

              {/* Left Arrow */}
              <div
                className={`swiper-button-prev-${idx} absolute left-[-60px] top-1/2 transform -translate-y-1/2 cursor-pointer bg-brand-teal text-white p-3 rounded-full z-10`}
              >
                <FaChevronLeft size={24} />
              </div>

              {/* Right Arrow */}
              <div
                className={`swiper-button-next-${idx} absolute right-[-60px] top-1/2 transform -translate-y-1/2 cursor-pointer bg-brand-teal text-white p-3 rounded-full z-10`}
              >
                <FaChevronRight size={24} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
